import { Stack, Text } from '@mantine/core'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import RootButton from '@/components/Buttons/RootButton'
import AddressDisplay from '@/components/User/AddressDisplay'
import { useAuth } from '@/plugins/auth'
import { getWeb3AuthInstance } from '@/plugins/auth/config'

interface ExportPrivKeyModalProps {
  close: () => void
}

const ExportPrivKeyModal: React.FC<ExportPrivKeyModalProps> = ({ close }) => {
  const { t } = useTranslation()
  const { provider } = useAuth()
  const [showPrivKey, setShowPrivKey] = useState<boolean>(false)
  const [privKey, setPrivKey] = useState<string>('')

  // TODO: Temporary solution. User is not supposed to have access to the priv key in the first place and the web3AuthInstance should not be used anywhere in the app
  const getPrivateKey = useCallback(async (): Promise<string> => {
    const p =
      provider ??
      (() => {
        const web3AuthInstance = getWeb3AuthInstance()
        if (!web3AuthInstance) {
          console.error('No web3AuthInstance found')
        }
        return web3AuthInstance?.provider
      })()

    try {
      const privateKey = await p?.request({
        method: 'eth_private_key',
      })
      return String(privateKey)
    } catch (error: any) {
      console.error('Error', error)
      throw new Error(`Error getting Web3Auth account private key: ${error.message}`)
    }
  }, [provider])

  const show = useCallback(async () => {
    const privateKey = await getPrivateKey()
    setPrivKey(privateKey)
    setShowPrivKey(previous => !previous)
  }, [getPrivateKey])

  return (
    <Stack style={{ flexDirection: 'column', gap: '20px' }}>
      <Text style={{ fontSize: '18px', marginRight: 1 }}>
        {t(
          'components.settings.privKeyModalText',
          'Are you sure you want to export the private key?'
        )}
      </Text>
      {showPrivKey && (
        <AddressDisplay
          address={privKey}
          showIdentity={false}
          truncated={false}
          showQrCode={false}
        />
      )}
      <Stack style={{ flexDirection: 'row', gap: '8px' }}>
        <RootButton secondary onClick={close} style1={false} expand>
          {t('buttons.cancel', 'Cancel')}
        </RootButton>
        <RootButton style1={false} onClick={show} expand>
          {showPrivKey ? t('buttons.hide', 'Hide') : t('buttons.show', 'Show')}
        </RootButton>
      </Stack>
    </Stack>
  )
}

export default ExportPrivKeyModal
